import axios from "axios";
import {AxiosInstance} from "axios";

/**
 * Executor Cypress instalado na máquina do QA (setup-panoverse-cypress.exe). Com "Abrir janela do navegador"
 * ligado a execução roda nele, chamado direto do navegador; sem janela, quem chama é o backend (executor do servidor).
 */
export const EXECUTOR_LOCAL_URL = 'http://127.0.0.1:4001';
export const EXECUTOR_SELENIUM_URL = 'http://127.0.0.1:4000';

// Quanto cada envio espera a resposta (validação do caso ou "execução iniciada")
const ESPERA_RESPOSTA_MS = 1000;
// Um por vez: os envios saem espaçados, na ordem do plano, e nunca passam do limite de conexões do navegador
const INTERVALO_ENVIO_MS = 250;

export interface EnvioCasos {
    aceitos: Array<number>,
    recusados: Array<{id: number, motivo: string}>,
    /** Nenhuma resposta do executor: ele não está aberto nesta máquina */
    semExecutor: boolean
}

const executorLocal = (): AxiosInstance => axios.create({baseURL: EXECUTOR_LOCAL_URL});

const esperar = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

/**
 * Qualquer resposta HTTP quer dizer que o executor está aberto. No Windows uma porta fechada leva uns 2s para recusar
 * a conexão, mais do que a espera de cada envio: sem esta checagem o envio encerrado por tempo passaria por entregue.
 */
const executorAberto = async () => {
    try {
        await executorLocal().get('recording-status', {timeout: 5000});
        return true;
    } catch (error: any) {
        return !!error?.response;
    }
}

/**
 * Manda os casos ao /execute-tester do executor desta máquina sem esperar o fim da execução.
 * Simultâneo: o executor inicia cada caso na hora e responde logo. Um por vez: o caso entra na fila do executor, que só
 * responde quando ele terminar; o envio é encerrado depois de entregue e o caso continua na fila.
 */
export const enviarCasosExecutorLocal = async (jobs: Array<any>, token: string, simultaneo: boolean): Promise<EnvioCasos> => {
    const envio: EnvioCasos = {aceitos: [], recusados: [], semExecutor: false};
    if (!(await executorAberto())) {
        return {aceitos: [], recusados: jobs.map(job => ({id: job.id, motivo: 'o executor desta máquina não respondeu.'})), semExecutor: true};
    }
    let respondeu = false;

    const enviar = async (job: any) => {
        try {
            const response = await executorLocal().post('execute-tester', {...job, token}, {timeout: ESPERA_RESPOSTA_MS});
            respondeu = true;
            if (response.data?.success) {
                envio.aceitos.push(job.id);
            } else {
                envio.recusados.push({id: job.id, motivo: response.data?.message || 'O executor recusou o caso.'});
            }
        } catch (error: any) {
            if (error?.code === 'ECONNABORTED') {
                // Entregue: o executor segura a resposta até o caso terminar
                respondeu = true;
                envio.aceitos.push(job.id);
            } else {
                envio.recusados.push({id: job.id, motivo: 'o executor desta máquina não respondeu em ' + EXECUTOR_LOCAL_URL + '.'});
            }
        }
    };

    await Promise.all(jobs.map(async (job, indice) => {
        if (!simultaneo) {
            await esperar(indice * INTERVALO_ENVIO_MS);
        }
        await enviar(job);
    }));
    envio.semExecutor = !respondeu;
    return envio;
}

/**
 * Selenium: um POST por caso, que só responde ao fim da execução.
 * Os casos são enviados um por vez; `deveParar` interrompe os que ainda não foram enviados.
 */
export const executarLegadoUmPorVez = async (baseURL: string, jobs: Array<any>, deveParar: () => boolean) => {
    const api = axios.create({baseURL});
    for (const job of jobs) {
        if (deveParar()) {
            break;
        }
        try {
            await api.post('execute-tester', job);
        } catch (error) {
            // o próprio executor fecha o resultado com erro; segue para o próximo caso
        }
    }
}

export const abrirCypressLocal = () => {
    executorLocal().get('open-cypress').catch(() => {});
}
